import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dialog, Field, Icon, Input, useErrorDialog, useToast } from '@dayon/design-system';
import type { CatalogueChangeReason, CatalogueWriteResult } from '@dayon/contracts';
import { api, ApiRequestError, isErrorCode } from '../../lib/api.ts';
import { ReasonFields } from './ReasonFields.tsx';
import {
  catalogueRefusalTitle,
  DEFAULT_EDIT_REASON,
  movesSentence,
  noteIsValid,
  percentToBasisPoints,
} from './catalogue-write.ts';

/** The provider being edited, as the Providers table already holds it. */
export interface PendingProviderConfig {
  id: string;
  code: string;
  name: string;
  /** 'YYYY-MM-DD' or null. Null is "not recorded", which is not a zero. */
  certifiedAt: string | null;
  revenueShareBp: number | null;
}

/**
 * A PROVIDER'S COMMERCIAL RECORD — `PATCH /api/games/providers/:id/config`.
 *
 * The two columns this tab reported as &ldquo;missing, not blank&rdquo; for its
 * whole life. They exist now, and the notice above the table changed in the same
 * commit — a screen describing capability it no longer lacks is the same defect
 * as one claiming capability it never had.
 *
 * RECORDED, NOT ENFORCED, and this dialog says so before the click. A
 * certification date gates nothing: no launch is refused for its absence, and
 * the write does not touch `POST /api/games/status`. The gate that exists is the
 * provider's own status, set deliberately with its own reason. Wiring a silent
 * second gate onto a date an operator can type would make "why is this game
 * refusing" answerable only by reading two tables.
 *
 * `code`, `name` and `driver` are not here. A code is referenced by every game
 * on the shelf and by the driver factory; a driver is code that either exists or
 * does not. A mistyped provider is disabled and replaced, which leaves both acts
 * in the log.
 */
export function ProviderConfigDialog({
  pending,
  holdsKey,
  mfa,
  onClose,
  onChanged,
}: {
  pending: PendingProviderConfig | null;
  holdsKey: boolean;
  mfa: boolean;
  onClose: () => void;
  onChanged: (result: CatalogueWriteResult) => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [certifiedAt, setCertifiedAt] = useState('');
  const [sharePercent, setSharePercent] = useState('');
  const [reasonCode, setReasonCode] = useState<CatalogueChangeReason>(DEFAULT_EDIT_REASON);
  const [note, setNote] = useState('');
  const [noteTouched, setNoteTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!pending) return;
    setCertifiedAt(pending.certifiedAt ?? '');
    setSharePercent(pending.revenueShareBp === null ? '' : String(pending.revenueShareBp / 100));
    setReasonCode(DEFAULT_EDIT_REASON);
    setNote('');
    setNoteTouched(false);
  }, [pending]);

  const share = percentToBasisPoints(sharePercent);

  /* Absent means "leave alone"; null means "clear". Both are real requests on
     both fields, and the empty box is the one that has to mean CLEAR here —
     the dialog was opened with the stored value in it, so an operator who
     emptied it did so on purpose. */
  const patch = useMemo<Record<string, unknown>>(() => {
    const next: Record<string, unknown> = {};
    if (!pending) return next;
    const nextDate = certifiedAt === '' ? null : certifiedAt;
    if (nextDate !== pending.certifiedAt) next['certifiedAt'] = nextDate;
    /* Parsed INSIDE the memo, from the raw string, so the dependency is what an
       operator typed rather than a fresh result object on every render.
       `undefined` is the INVALID case and must not be sent as either value: an
       unparseable percentage is neither a change nor a clear, and the error is
       shown on the field instead. */
    const typed = percentToBasisPoints(sharePercent);
    const nextShare = typed.kind === 'value' ? typed.bp : typed.kind === 'absent' ? null : undefined;
    if (nextShare !== undefined && nextShare !== pending.revenueShareBp)
      next['revenueShareBp'] = nextShare;
    return next;
    /* Memoised for the reason GameConfigDialog's is: `submit` closes over it, so
       a fresh object every render rebuilds `submit` on every keystroke. */
  }, [pending, certifiedAt, sharePercent]);
  const movedCount = Object.keys(patch).length;
  const ready = pending !== null && movedCount > 0 && noteIsValid(note) && share.kind !== 'invalid';

  const submit = useCallback(async () => {
    if (!pending || !ready) return;
    setSubmitting(true);
    try {
      const result = await api.patch<CatalogueWriteResult>(
        `/api/games/providers/${pending.id}/config`,
        { ...patch, reasonCode, note: note.trim() },
      );
      toast(movesSentence(result));
      onChanged(result);
      onClose();
    } catch (error: unknown) {
      const raw = error instanceof ApiRequestError ? error.code : undefined;
      showError({
        title: catalogueRefusalTitle(isErrorCode(raw) ? raw : undefined, pending.name, {
          allowed: holdsKey,
          mfa,
        }),
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was written. Try again shortly.',
        ...(raw ? { code: raw } : {}),
      });
    } finally {
      setSubmitting(false);
    }
  }, [pending, ready, patch, reasonCode, note, holdsKey, mfa, onChanged, onClose, showError, toast]);

  return (
    <Dialog
      open={pending !== null}
      onClose={onClose}
      title={pending ? `Edit ${pending.name}'s record` : ''}
      dismissible={!submitting}
      actions={
        <>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={submitting}
            disabled={!ready || submitting}
            onClick={() => void submit()}
          >
            {movedCount === 0
              ? 'Nothing to save'
              : `Save ${String(movedCount)} change${movedCount === 1 ? '' : 's'}`}
          </Button>
        </>
      }
    >
      {pending && (
        <div className="stack">
          <div className="notice notice-quiet">
            <Icon name="warning-circle" />
            <div>
              <strong>Both fields are recorded, not enforced.</strong> No launch is refused for a
              missing certification date and no settlement job reads the revenue share yet. The gate
              that stops a shelf is the provider&rsquo;s status, which is changed from the table with
              its own reason. Everything typed here appears in the Configuration log.
            </div>
          </div>

          {movedCount === 0 && (
            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                Nothing has been changed yet. An edit that moves no field is not recorded — the
                platform refuses it rather than writing a log row nobody made.
              </div>
            </div>
          )}

          <Field
            label="Certified on"
            hint="Empty means “not recorded”, and clearing a date that was set is a real change the log will carry."
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                type="date"
                aria-describedby={describedBy}
                value={certifiedAt}
                onChange={(e) => setCertifiedAt(e.target.value)}
              />
            )}
          </Field>

          <Field
            label="Revenue share (% of GGR)"
            error={share.kind === 'invalid' ? share.why : undefined}
            hint="Empty means “not recorded”, which is not the same as 0% — a provider that costs nothing is a real term. Stored as basis points, so two decimals."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                type="number"
                step="0.01"
                min="0"
                max="100"
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={sharePercent}
                onChange={(e) => setSharePercent(e.target.value)}
                placeholder="12.5"
              />
            )}
          </Field>

          <ReasonFields
            reasonCode={reasonCode}
            onReasonCode={setReasonCode}
            note={note}
            onNote={setNote}
            noteTouched={noteTouched}
            onNoteBlur={() => setNoteTouched(true)}
            placeholder="Renegotiated to 15% of GGR from 1 October, per the signed addendum."
          />
        </div>
      )}
    </Dialog>
  );
}
